import { useCallback } from 'react';
import { useActiveViewableAssetId } from '@frameio/shared/hooks';
import { useSetDocumentPositionMutation } from '@frameio/shared/integrations/gql';
import {
  getAfterEffectsCommentMarkerStartMicroseconds,
  useAfterEffectsCommentMarkerSyncState,
} from './markerSync/aeCommentMarkerSyncConfig';

/**
 * Sets the After Effects composition playhead when the user selects a comment
 * timestamp in the media viewer (shared `setDocumentPosition` UXP mutation).
 * Timestamps are shifted by the matching export's `workAreaStart` when present so
 * they match composition time (same basis as marker sync).
 */
export const useSetCompositionPosition = () => {
  const [setDocumentPositionMutation] = useSetDocumentPositionMutation({
    context: { resolveWithUXP: true },
  });
  const viewableAssetId = useActiveViewableAssetId();
  const { getExportMetadataForAsset } = useAfterEffectsCommentMarkerSyncState({
    assetId: viewableAssetId,
  });

  const setCompositionPosition = useCallback(
    ({
      commentId,
      timestampMicroseconds,
      assetId,
    }: {
      commentId: string;
      timestampMicroseconds: number;
      assetId?: string | null;
    }) => {
      if (!assetId) {
        return;
      }

      const exportMetadata = getExportMetadataForAsset(assetId);
      const timelineMicroseconds =
        getAfterEffectsCommentMarkerStartMicroseconds(
          { timestampMicroseconds },
          exportMetadata
        );

      setDocumentPositionMutation({
        variables: {
          input: {
            assetId,
            commentId,
            timestampMicroseconds: timelineMicroseconds,
          },
        },
      });
    },
    [getExportMetadataForAsset, setDocumentPositionMutation]
  );

  return { setCompositionPosition };
};
